/**
 * Get + update a single Binom offer — backs the edit dialog.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';
import { binomKeys } from './useBinom';

export interface BinomOfferDetail {
  id: number | string;
  name: string;
  url?: string;
  countryCode?: string;
  affiliateNetworkId?: number | string;
  groupUuid?: string;
  payout?: unknown;
  [key: string]: unknown;
}

const offerKey = (id: string) =>
  [...binomKeys.all, 'offer', id] as const;

export function useBinomOffer(id: string | null) {
  return useQuery({
    queryKey: offerKey(id ?? ''),
    queryFn: async () => {
      const r = await api.get<{ data: BinomOfferDetail }>(
        `/binom/offers/${encodeURIComponent(id!)}`,
      );
      return r.data?.data ?? null;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useUpdateBinomOffer(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BinomOfferDetail) => {
      const r = await api.put<{ data: { success: boolean } }>(
        `/binom/offers/${encodeURIComponent(id!)}`,
        body,
      );
      return r.data?.data;
    },
    onSuccess: () => {
      if (id) qc.invalidateQueries({ queryKey: offerKey(id) });
    },
  });
}
